import { Doc } from '@dadohr/shared/domainTypes/document';
import { NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  IExperienceTemplate,
  isEventDrivenTiming
} from '@dadohr/shared/domainTypes/experience';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { Theme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  ButtonBase,
  ButtonProps,
  IconButton,
  Tooltip,
  Typography
} from '@material-ui/core';
import { assertNever } from 'assert-never';
import { partition } from 'lodash';
import { CheckCircle, Mail, Search, X, Zap } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ChipFilterWithDropdown } from '../../../../components/ChipFilterWithDropdown';
import { TableSearch } from '../../../../components/Table';
import { useDialogState } from '../../../../hooks/useDialogState';
import {
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { LightMdToJsx } from '../../../../services/formatter';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { MatchedItem, searchInTemplate } from '../../../../services/search';
import { ONE_DAY } from '../../../../services/time';
import { useTheme } from '../../../../themes';
import { ActorDescriptor, useActorDescriptorsFromTemplate } from '../Actors';
import { ChannelIcons } from '../Channels';
import { StandardDrawer } from '../StandardDrawer';

const ResultsContainer = styled('div')((p) => ({
  marginTop: p.theme.spacing(2)
}));

const ResultsHeading = styled('div')((p) => ({
  marginBottom: p.theme.spacing()
}));

const ItemContainer = styled('div')((p) => ({
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(1)}px`,
  cursor: 'pointer',
  ':hover': {
    background: p.theme.custom.colors.gray.w100
  },
  borderBottom: `${p.theme.custom.border.standard}`
}));

const ShowMoreButtonContainer = styled('div')((p) => ({
  color: p.theme.custom.colors.blue.w400
}));

const IconContainer = styled('div')<{ color: string }>((p) => ({
  color: p.color,
  // This seemingly random margin helps to align the icon better with the text next to it, as the text has a natural line height
  marginTop: 2
}));

const ItemIcon = ({ d }: { d: MatchedItem }) => {
  const iconSize = 16;
  if (d.type === 'MSG') {
    return <Mail size={iconSize} />;
  }

  if (d.type === 'TASK') {
    return <CheckCircle size={iconSize} />;
  }

  if (d.type === 'AUTOMATION') {
    return <Zap size={iconSize} />;
  }

  return null;
};

const getItemStart = (d: MatchedItem, t: UnsafeTFunction): string => {
  // TODO Triggered Items - tja, what do we write here?
  if (isEventDrivenTiming(d.item.timing)) {
    return t('Triggered');
  }
  if (d.type === 'TASK') {
    return t('Day {{ day }}', {
      day: Math.floor(d.item.timing.start / ONE_DAY) + 1
    });
  }
  if (d.type === 'MSG') {
    return t('Day {{ day }}', {
      day: Math.floor(d.item.timing.start / ONE_DAY) + 1
    });
  }

  if (d.type === 'AUTOMATION') {
    return t('Day {{ day }}', {
      day: Math.floor(d.item.timing.start / ONE_DAY) + 1
    });
  }

  return '';
};

const Item = ({
  d,
  onSelect,
  onShowMoreResults,
  iconProps
}: {
  d: MatchedItem;
  onSelect: () => void;
  onShowMoreResults?: () => void;
  iconProps: {
    color: string;
    tooltip: string;
  };
}) => {
  const { t } = useUnsafeTranslation();
  const [showAllResults, setShowAllResults] = useState(false);
  const matches = d.matches.filter((m) => m.blockId !== 'TITLE');
  const matchesToShow = showAllResults ? matches : matches.slice(0, 1);
  const itemStart = getItemStart(d, t);
  const titleMatch = d.matches.find((x) => x.blockId === 'TITLE');
  // We use this as context matches come formatted with bolding of matches etc
  const itemTitle = titleMatch
    ? titleMatch.match.withContext.txt
    : d.type === 'MSG'
    ? d.item.subject
    : d.item.title;
  return (
    <ItemContainer role="button" onClick={onSelect}>
      <FlexContainer justifyContent="flex-start" alignItems="flex-start">
        <div>
          <Tooltip title={iconProps.tooltip}>
            <IconContainer color={iconProps.color}>
              <ItemIcon d={d} />
            </IconContainer>
          </Tooltip>
        </div>
        <FlexContainer spacing={1} direction="column" alignItems="start">
          <FlexContainer spacing={0.5} direction="column" alignItems="start">
            <LightMdToJsx>{itemTitle}</LightMdToJsx>
            <Typography variant="caption">
              <FlexContainer spacing={0.5}>
                {itemStart && <div>{itemStart}</div>}
                {d.type !== 'AUTOMATION' && (
                  <>
                    {itemStart && <div>•</div>}
                    <ChannelIcons size={14} channels={d.item.channels} />
                  </>
                )}
              </FlexContainer>
            </Typography>
          </FlexContainer>
          {!!matches.length && (
            <FlexContainer spacing={0.5} direction="column" alignItems="start">
              {matchesToShow.map((m, i) => (
                <LightMdToJsx key={i}>{m.match.withContext.txt}</LightMdToJsx>
              ))}
              {!showAllResults && matches.length > 1 && (
                <ShowMoreButtonContainer>
                  <ButtonBase
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onShowMoreResults?.();
                      setShowAllResults(true);
                    }}
                  >
                    {t('+ {{ count }} more {{ result }}', {
                      count: matches.length - 1,
                      result:
                        matches.length - 1 === 1 ? t('result') : t('results')
                    })}
                  </ButtonBase>
                </ShowMoreButtonContainer>
              )}
            </FlexContainer>
          )}
        </FlexContainer>
      </FlexContainer>
    </ItemContainer>
  );
};

const getIconProps = (
  d: MatchedItem,
  actorDescriptors: { [actorKey: string]: ActorDescriptor },
  theme: Theme,
  t: UnsafeTFunction
): {
  color: string;
  tooltip: string;
} => {
  switch (d.type) {
    case 'AUTOMATION':
      return {
        color: theme.custom.colors.purple.w500,
        tooltip: `${t('Automation')}`
      };
    case 'MSG':
      const msgActor = actorDescriptors[d.item.actor];
      return {
        color: msgActor?.color || '',
        tooltip: `${t('Message to {{ actor }}', {
          actor: msgActor?.label || ''
        })}`
      };
    case 'TASK':
      const taskActor = actorDescriptors[d.item.actor];
      return {
        color: taskActor?.color || '',
        tooltip: `${t('Task for {{ actor }}', {
          actor: taskActor?.label || ''
        })}`
      };
    default:
      return assertNever(d);
  }
};

const MINIMUM_SEARCH_LENGTH = 3;
export const TemplateSearchDrawer = ({
  tpl,
  open,
  onClose,
  onSelectMatch,
  onSearch = NOOP,
  onMoreResultsClicked = NOOP,
  onFilter
}: {
  tpl: Doc<IExperienceTemplate>;
  open: boolean;
  onClose: () => void;
  onSelectMatch: (match: MatchedItem, i: number) => void;
  onSearch?: (q: string, noOfResults: number) => void;
  onMoreResultsClicked?: (match: MatchedItem, i: number) => void;
  onFilter?: (
    type: 'ACTOR' | 'ITEM_TYPE',
    selected: {
      key: string;
      label: string;
    }[],
    deselected: { key: string; label: string }[]
  ) => void;
}) => {
  const { t } = useUnsafeTranslation();

  const [q, setQ] = useState(''); // better to store in url - but introduces race conditions again...

  const minimumSearchLengthReached = q.length >= MINIMUM_SEARCH_LENGTH;

  const theme = useTheme();
  const actorDescriptors = useActorDescriptorsFromTemplate(
    tpl.data.supportingActors
  );

  const actorOptions = Object.values(actorDescriptors).map((a) => ({
    key: a.key,
    label: a.label
  }));
  const [selectedActors, setSelectedActors] = useState(actorOptions);

  const typeOptions: {
    key: 'MSG' | 'AUTOMATION' | 'TASK';
    label: string;
  }[] = useMemo(
    () => [
      { key: 'MSG', label: t('Message') },
      { key: 'TASK', label: t('Task') },
      { key: 'AUTOMATION', label: t('Automation') }
    ],
    [t]
  );

  const [selectedType, setSelectedType] = useState(typeOptions);

  const results = useMemo(() => {
    if (!minimumSearchLengthReached) {
      return [];
    }
    const nextResults = searchInTemplate(tpl, q, {
      contextLength: 54,
      filterByActor: selectedActors.map((a) => a.key),
      filterByType: selectedType.map((a) => a.key)
    });
    onSearch(q, nextResults.length);
    return nextResults;
  }, [
    tpl,
    q,
    minimumSearchLengthReached,
    onSearch,
    selectedType,
    selectedActors
  ]);

  return (
    <StandardDrawer open={open} onClose={onClose}>
      <FlexContainer justifyContent="space-between" marginBottom={2}>
        <Typography variant="h5" component="h5">
          {t('Search')}
        </Typography>
        <IconButton onClick={onClose}>
          <X size={16} />
        </IconButton>
      </FlexContainer>
      <TableSearch term={q} onChange={setQ} autoFocus={true} placeholder="" />
      <FlexContainer direction="column" alignItems="flex-start" marginTop={2}>
        <FlexContainer>
          <ChipFilterWithDropdown
            label={t('Filter by actor')}
            options={actorOptions}
            selectedOptions={selectedActors}
            onChange={(nextSelected) => {
              if (onFilter) {
                const [selected, deselected] = partition(actorOptions, (o) =>
                  nextSelected.find((x) => x.key === o.key)
                );
                onFilter('ACTOR', selected, deselected);
              }

              setSelectedActors(nextSelected);
            }}
          />
          <ChipFilterWithDropdown
            label={t('Filter by item type')}
            options={typeOptions}
            selectedOptions={selectedType}
            onChange={(nextSelected) => {
              if (onFilter) {
                const [selected, deselected] = partition(typeOptions, (o) =>
                  nextSelected.find((x) => x.key === o.key)
                );
                onFilter('ITEM_TYPE', selected, deselected);
              }
              setSelectedType(nextSelected);
            }}
          />
        </FlexContainer>
      </FlexContainer>
      <ResultsContainer>
        <ResultsHeading>
          <Typography variant="caption">{t('Results')}</Typography>
          {!results.length && (
            <FlexContainer justifyContent="center" marginTop={4}>
              <Typography variant="caption">
                <i>
                  {minimumSearchLengthReached
                    ? t('No matches found')
                    : t('Please type at least {{ minCount }} characters', {
                        minCount: MINIMUM_SEARCH_LENGTH
                      })}
                </i>
              </Typography>
            </FlexContainer>
          )}
          {results.map((d, i) => (
            <Item
              key={i}
              d={d}
              onSelect={() => onSelectMatch(d, i)}
              onShowMoreResults={() => onMoreResultsClicked(d, i)}
              iconProps={getIconProps(d, actorDescriptors, theme, t)}
            />
          ))}
        </ResultsHeading>
      </ResultsContainer>
    </StandardDrawer>
  );
};

export const TemplateSearchButton = ({
  tpl,
  onSelectMatch,
  ...buttonProps
}: {
  tpl: Doc<IExperienceTemplate>;
  onSelectMatch: (match: MatchedItem) => void;
} & Omit<ButtonProps, 'onClick' | 'href'>) => {
  const { t } = useTranslation();
  const { trackEvent } = useProductAnalytics();
  const {
    dialogOpen: drawerOpen,
    openDialog: openDrawer,
    closeDialog: closeDrawer
  } = useDialogState();

  const trackingFns = useMemo(() => {
    return {
      onOpen: () =>
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Template Search Button Clicked',
          Explanation: `Admin clicks on the 'search template content' button`,
          'Experience Template ID': tpl.id
        }),
      onSelect: (match: MatchedItem, i: number) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Template Search Result Clicked',
          Explanation:
            'Admin clicks on a result after searching template content',
          'Experience Template ID': tpl.id,
          'Timeline Item ID': match.item.id,
          'Timeline Item Title':
            match.type === 'MSG' ? match.item.subject : match.item.title,
          'Timeline Item Type': match.type === 'MSG' ? 'MESSAGE' : match.type,
          'Result List Position': i + 1
        });
      },
      onShowMoreResults: (match: MatchedItem, i: number) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Template Search See More Results Clicked',
          Explanation: `Admin clicks on the 'see more results' link for an individual search result`,
          'Experience Template ID': tpl.id,
          'Timeline Item ID': match.item.id,
          'Timeline Item Title':
            match.type === 'MSG' ? match.item.subject : match.item.title,
          'Timeline Item Type': match.type === 'MSG' ? 'MESSAGE' : match.type,
          'Result List Position': i + 1
        });
      },
      onSearch: (q: string, noOfResults: number) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Template Search Run',
          Explanation: 'Admin conducts a search of template content',
          'Experience Template ID': tpl.id,
          'Search String': q,
          'Number Of Results': noOfResults
        });
      },
      onFilter: (
        type: 'ACTOR' | 'ITEM_TYPE',
        selected: { key: string; label: string }[],
        deselected: { key: string; label: string }[]
      ) => {
        trackEvent({
          eventType: PAEventType.EXPERIENCE_TPL,
          name: 'Search Filter Applied',
          Explanation: 'Admin applied a filter to timeline search results',
          'Experience Template ID': tpl.id,
          'Filter Type':
            type === 'ACTOR'
              ? 'Supporting Actors'
              : type === 'ITEM_TYPE'
              ? 'Item Types'
              : assertNever(type),
          'Selected Filters': selected.map((a) => a.label).join(', '),
          'Deselected Filters': deselected.map((a) => a.label).join(',')
        });
      }
    };
  }, [tpl, trackEvent]);

  return (
    <>
      <Button
        startIcon={<Search size={16} />}
        {...buttonProps}
        onClick={() => {
          trackingFns.onOpen();
          openDrawer();
        }}
      >
        {buttonProps.children || t('Search')}
      </Button>
      <TemplateSearchDrawer
        tpl={tpl}
        open={drawerOpen}
        onClose={closeDrawer}
        onSelectMatch={(match, i) => {
          trackingFns.onSelect(match, i);
          onSelectMatch(match);
        }}
        onSearch={trackingFns.onSearch}
        onMoreResultsClicked={trackingFns.onShowMoreResults}
        onFilter={trackingFns.onFilter}
      />
    </>
  );
};
